import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";

import reportService from "../../services/reportService";

const PAGE_SIZE = 10;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const dateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : "-";

const DealerReports = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const loadTransfers = async () => {
    try {
      setLoading(true);

      const response =
        await reportService.getTransactionReport();

      const transactions =
        Array.isArray(response?.transactions)
          ? response.transactions
          : [];

      const transfers = transactions.filter(
        (row) =>
          String(row.type || "").toUpperCase() ===
          "TRANSFER"
      );

      setRows(transfers);
    } catch (error) {
      setRows([]);

      toast.error(
        error.response?.data?.message ||
          "Unable to load Shop transfers."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransfers();
  }, []);

  const filtered = useMemo(() => {
    const term =
      search.trim().toLowerCase();

    if (!term) {
      return rows;
    }

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term)
      )
    );
  }, [rows, search]);

  const totalAmount = useMemo(
    () =>
      rows.reduce(
        (sum, row) =>
          sum + Number(row.amount || 0),
        0
      ),
    [rows]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PAGE_SIZE)
  );

  const currentRows = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">

      <Navbar
        toggleSidebar={() =>
          setSidebarOpen(
            (value) => !value
          )
        }
      />

      <div className="d-flex flex-grow-1 position-relative">

        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={() =>
            setSidebarOpen(false)
          }
        />

        <main className="app-main flex-grow-1 overflow-auto">

          <div className="container-fluid admin-page-wrap">

            <div className="admin-page-header">

              <div>
                <span className="admin-page-kicker">
                  DEALER REPORTS
                </span>

                <h2>Shop Transfers</h2>

                <p>
                  Review coin transfers made
                  to your Shops.
                </p>
              </div>

              <button
                className="btn btn-outline-primary"
                onClick={loadTransfers}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-2" />
                Refresh
              </button>

            </div>

            <div className="row g-3 mb-4">

              <div className="col-6 col-xl-4">
                <div className="admin-stat-card h-100">

                  <span className="admin-stat-icon tone-primary">
                    <i className="bi bi-arrow-left-right" />
                  </span>

                  <div>
                    <div className="admin-stat-label">
                      TOTAL TRANSFERS
                    </div>

                    <div className="admin-stat-value">
                      {rows.length}
                    </div>
                  </div>

                </div>
              </div>

              <div className="col-6 col-xl-4">
                <div className="admin-stat-card h-100">

                  <span className="admin-stat-icon tone-success">
                    <i className="bi bi-coin" />
                  </span>

                  <div>
                    <div className="admin-stat-label">
                      COIN VOLUME
                    </div>

                    <div className="admin-stat-value admin-stat-value-sm">
                      🪙 {money(totalAmount)}
                    </div>
                  </div>

                </div>
              </div>

              <div className="col-6 col-xl-4">
                <div className="admin-stat-card h-100">

                  <span className="admin-stat-icon tone-info">
                    <i className="bi bi-filter" />
                  </span>

                  <div>
                    <div className="admin-stat-label">
                      VISIBLE RESULTS
                    </div>

                    <div className="admin-stat-value">
                      {filtered.length}
                    </div>
                  </div>

                </div>
              </div>

            </div>

            <div className="admin-panel">

              <div className="admin-panel-head flex-wrap gap-3">

                <div>
                  <h5>Shop Transfers</h5>

                  <p>
                    Search and review your Shop
                    transfer history.
                  </p>
                </div>

                <div className="admin-search-wrap">

                  <i className="bi bi-search" />

                  <input
                    value={search}
                    onChange={(event) => {
                      setSearch(
                        event.target.value
                      );

                      setPage(1);
                    }}
                    placeholder="Search transfers..."
                  />

                </div>

              </div>

              {loading ? (

                <div className="p-5">
                  <Loader message="Loading Shop transfers..." />
                </div>

              ) : currentRows.length === 0 ? (

                <div className="admin-empty-state">

                  <i className="bi bi-inbox" />

                  <strong>
                    No Shop transfers found
                  </strong>

                  <span>
                    There are no transfer
                    records to display.
                  </span>

                </div>

              ) : (

                <div className="table-responsive">

                  <table className="table admin-table align-middle mb-0">

                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Dealer</th>
                        <th>Shop</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Note</th>
                        <th>Date</th>
                      </tr>
                    </thead>

                    <tbody>

                      {currentRows.map(
                        (row) => (

                          <tr key={row.id}>

                            <td>
                              #{row.id}
                            </td>

                            <td>
                              {row.sender_username ||
                                row.sender ||
                                "N/A"}
                            </td>

                            <td>
                              <strong>
                                {row.receiver_username ||
                                  row.receiver ||
                                  "N/A"}
                              </strong>
                            </td>

                            <td className="fw-bold text-success">
                              🪙 {money(row.amount)}
                            </td>

                            <td>
                              <span className="report-selection">
                                {row.type || "TRANSFER"}
                              </span>
                            </td>

                            <td>
                              <small>
                                {row.note || "-"}
                              </small>
                            </td>

                            <td>
                              <small>
                                {dateTime(
                                  row.created_at
                                )}
                              </small>
                            </td>

                          </tr>
                        )
                      )}

                    </tbody>

                  </table>

                </div>
              )}

              {!loading &&
                filtered.length >
                  PAGE_SIZE && (

                  <div className="admin-pagination-wrap">

                    <span>
                      Showing{" "}
                      {(page - 1) *
                        PAGE_SIZE +
                        1}
                      –
                      {Math.min(
                        page * PAGE_SIZE,
                        filtered.length
                      )}{" "}
                      of {filtered.length}
                    </span>

                    <Pagination
                      currentPage={page}
                      totalPages={
                        totalPages
                      }
                      onPageChange={
                        setPage
                      }
                    />

                  </div>
                )}

            </div>

          </div>

        </main>

      </div>

    </div>
  );
};

export default DealerReports;